import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Select, Store } from '@ngxs/store';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { skipWhile, take, takeUntil } from 'rxjs/operators';
import { SetCurrentSchoolYear } from 'src/shared/store/actions/school-year.action';
import { Logout } from './../../../shared/store/actions/auth.action';

import { BaseComponent } from '../../../shared/abstracts/base.abstract';
import { ModalRef } from '../../../shared/abstracts/modal-ref';
import { PreventBackModalComponent } from '../../../shared/components';
import { featuresWithPermissions } from '../../../shared/data/permissions';
import { urlBlackList } from '../../../shared/data/url-black-list';
import { EntityTypeEnum, ModalParentIdEnum, ModalType, TargetChoicesEnum } from '../../../shared/enums';
import { IFeatureList, ISchool, IUrlWithPermission, IUser } from '../../../shared/interfaces';
import { ModalService, PermissionsService } from '../../../shared/services';
import { ResetSelectedSchool, SetCurrentUser } from '../../../shared/store/actions/auth.action';
import { ResetPreventGoBack } from '../../../shared/store/actions/back-button.action';
import { GetAllClasses } from '../../../shared/store/actions/classes.action';
import { ResetEventsData } from '../../../shared/store/actions/events.action';
import { GetAllGroups } from '../../../shared/store/actions/groups.action';
import { ToggleMenu } from '../../../shared/store/actions/menu.action';
import { GetAllSchoolYear } from '../../../shared/store/actions/school-year.action';
import { GetAllSchools } from '../../../shared/store/actions/schools.action';
import { GetAllStudents, GetAllUsersLight, ResetCustody, ResetWaitingCustody } from '../../../shared/store/actions/users.action';
import { AuthState } from '../../../shared/store/state/auth.state';
import { BackButtonState } from '../../../shared/store/state/back-button.state';
import { UsersState } from '../../../shared/store/state/users.state';
import { LoggedInProfileComponent } from '../logged-in-profile/logged-in-profile.component';
import { SelectUserModalComponent } from '../select-user-modal/select-user-modal.component';
import { SupportBetaComponent } from '../support-beta/support-beta.component';
import { ViewSchoolComponent } from '../view-school/view-school.component';
import { SupportFormComponent } from './../support-form/support-form.component';

@Component({
    selector: 'app-header-logged-in',
    templateUrl: './header-logged-in.component.html',
    styleUrls: ['./header-logged-in.component.scss'],
})
export class HeaderLoggedInComponent extends BaseComponent implements OnInit {
    @Select(UsersState.getAllStudents) public students$: Observable<IUser[]>;
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;
    @Select(AuthState.user) public user$: Observable<IUser>;
    @Select(AuthState.currentSchool) public currentSchool$: Observable<ISchool>;

    public permissions: IFeatureList = featuresWithPermissions;

    public user: IUser;
    public isUserInMultipleSchools: boolean = false;
    public showLogoutOptions = false;
    public hasClickOnLogout = false;

    public targetChoicesEnum = TargetChoicesEnum;
    public currentTarget: TargetChoicesEnum;

    private currentSchool: ISchool;
    private currentUser: IUser;
    private students: IUser[];
    private routeToGo: string;
    private paramToUse: any;
    private url: string;
    private userToSelect: IUser;

    constructor(
        private store: Store,
        private router: Router,
        private localize: LocalizeRouterService,
        private cookies: CookieService,
        private modalService: ModalService,
        private permissionService: PermissionsService,
        private location: Location
    ) {
        super();
    }

    public ngOnInit(): void {
        this.store.dispatch(new SetCurrentSchoolYear());
        this.uns = this.user$
            .pipe(
                skipWhile((user: IUser) => user === null),
                take(1)
            )
            .subscribe((user: IUser) => {
                this.user = user;
                this.isUserInMultipleSchools =
                    (user.schoolIds?.length > 1 && user.type !== EntityTypeEnum.PARENT && user.type !== EntityTypeEnum.STUDENT) ||
                    user.type === EntityTypeEnum.ADMINISTRATOR;
                this.store.dispatch(new GetAllStudents());
            });
        this.uns = this.currentSchool$.subscribe((school) => (this.currentSchool = school));
        this.uns = this.currentUser$.subscribe((currentUser) => (this.currentUser = currentUser));
        this.uns = this.students$.subscribe((students) => (this.students = students));

        if (!(this.cookies.get('hideSupport') === 'true')) {
            // this.openModalBetaSupport();
        }

        this.uns = this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationEnd) {
                this.url = event.url.replace(/(\/[a-z]*)/, '');
                this.url = this.url.replace(/[?].*/, '');
            }
        });
    }

    public goToRoute(route: string, param: any): void {
        this.routeToGo = route;
        this.paramToUse = param;
        if (this.store.selectSnapshot(BackButtonState.getPreventGoBack)) {
            this.currentTarget = TargetChoicesEnum.ROUTING;
            this.openModalPreventBack();
        } else {
            this.navigateToView();
        }
    }

    public navigateToView(): void {
        this.modalService.closeModal();
        this.router.navigate([this.localize.translateRoute(this.routeToGo)], { queryParams: this.paramToUse });
    }

    public toggleMenu(): void {
        this.store.dispatch(new ToggleMenu());
    }

    public toggleLogoutOptions(toggleFromLogout: boolean): void {
        console.log('toggleLogoutOptions', toggleFromLogout);
        console.log('hasClickOnLogout', this.hasClickOnLogout);
        console.log('showLogoutOptions', this.showLogoutOptions);
        if (toggleFromLogout) {
            this.hasClickOnLogout = true;
            this.showLogoutOptions = !this.showLogoutOptions;
        } else if (!toggleFromLogout && this.hasClickOnLogout) {
            this.hasClickOnLogout = false;
            this.showLogoutOptions = true;
        } else {
            this.showLogoutOptions = false;
        }
    }

    public preventLogout(): boolean {
        if (this.store.selectSnapshot(BackButtonState.getPreventGoBack)) {
            this.currentTarget = TargetChoicesEnum.LOGOUT_PREVENT_BACK;
        } else {
            this.currentTarget = TargetChoicesEnum.LOGOUT;
        }
        this.openModalPreventBack();
        return false;
    }

    public logout(): void {
        if (this.showLogoutOptions) {
            this.toggleLogoutOptions(false);
        }
        this.store.dispatch([new ResetPreventGoBack()]).subscribe(() => {
            this.store.dispatch(new Logout());
        });
    }

    public preventSchoolChange(): boolean {
        this.currentTarget = TargetChoicesEnum.SCHOOL;
        this.openModalPreventBack();
        return false;
    }

    public closePreventBack(answer: boolean): void {
        this.modalService.closeModal();
        if (answer) {
            this.store.dispatch(new ResetPreventGoBack());
            switch (this.currentTarget) {
                case TargetChoicesEnum.SCHOOL:
                    this.changeSchool();
                    break;
                case TargetChoicesEnum.IMPERSONATE:
                    this.changeUser(this.userToSelect);
                    break;
                case TargetChoicesEnum.ROUTING:
                    this.navigateToView();
                    break;
                case TargetChoicesEnum.LOGOUT:
                case TargetChoicesEnum.LOGOUT_PREVENT_BACK:
                    this.logout();
                    break;
            }
        }
    }

    public changeSchool(): boolean {
        if (this.showLogoutOptions) {
            this.toggleLogoutOptions(false);
        }
        this.store.dispatch(new ResetSelectedSchool()).subscribe(() => {
            this.router.navigate([this.localize.translateRoute('/auth/select-dashboard')]);
        });
        return false;
    }

    public changeUser(user: IUser): void {
        if (this.router.url.includes('/my-account/')) {
            this.location.back();
        }

        // si on édite/crée un student et qu'on change de student, retour vers répertoire
        if (
            (this.router.url.includes('/directory/edit/student') || this.router.url.includes('/directory/add/student')) &&
            user.type === EntityTypeEnum.STUDENT
        ) {
            this.router.navigate([this.localize.translateRoute('/directory')]);
        }

        const urlWithPermission: IUrlWithPermission = urlBlackList.find((item: IUrlWithPermission) => item.url === this.url);

        if (urlWithPermission) {
            const hasAccess: boolean = this.permissionService.checkPermission(
                this.user.accessLevel,
                user.accessLevel,
                urlWithPermission.permission
            );

            if (!hasAccess) {
                this.router.navigate([this.localize.translateRoute(urlWithPermission.redirection)]);
            }
        }

        const dispachActions: any[] = [
            new GetAllStudents(),
            new GetAllUsersLight(),
            new GetAllSchools(),
            new GetAllClasses(true),
            new GetAllSchoolYear(),
            new GetAllGroups(true),
        ];

        this.store
            .dispatch([new ResetEventsData(), new ResetCustody(), new ResetWaitingCustody(), new SetCurrentUser(user)])
            .subscribe(() => {
                this.store.dispatch(dispachActions);
            });
        this.modalService.closeModal();
    }

    public openCalendarModal(student: IUser): void {
        this.router.navigate([this.localize.translateRoute('/directory/edit/student')], {
            queryParams: { id: student.id, openCalendar: true },
        });
    }

    public openModalSelectUser(): void {
        const modal: ModalRef<SelectUserModalComponent> = this.modalService.openModalWithComponent({
            type: ModalType.STANDARD,
            body: {
                title: 'NAV.HEADERS.MODAL.SELECT-USER.TITLE',
            },
            component: {
                componentRef: SelectUserModalComponent,
                parentIdRef: ModalParentIdEnum.BODY_CONTENT,
                config: {
                    inputs: {
                        currentUser: this.currentUser,
                        user: this.user,
                        students: this.students,
                        url: this.url,
                    },
                },
            },
        });
        modal
            .getComponent()
            .goToRouteEmitter.pipe(takeUntil(modal.onClose))
            .subscribe(({ route, param }) => {
                this.modalService.closeModal();
                this.goToRoute(route, param);
            });
        modal
            .getComponent()
            .openCalendarEmitter.pipe(takeUntil(modal.onClose))
            .subscribe((student: IUser) => {
                this.modalService.closeModal();
                this.openCalendarModal(student);
            });
        modal
            .getComponent()
            .selectUserEmitter.pipe(takeUntil(modal.onClose))
            .subscribe((student: IUser) => {
                this.modalService.closeModal();
                this.selectUser(student);
            });
    }

    public openModalLoggedInProfile(): boolean {
        const modal: ModalRef<LoggedInProfileComponent> = this.modalService.openModalWithComponent({
            type: ModalType.STANDARD,
            body: {
                title: this.user.firstname + ' ' + this.user.lastname,
            },
            component: {
                componentRef: LoggedInProfileComponent,
                parentIdRef: ModalParentIdEnum.BODY_CONTENT,
                config: {
                    inputs: {
                        contact: this.user,
                        type: this.user.type,
                    },
                },
            },
        });
        modal
            .getComponent()
            .clicked.pipe(takeUntil(modal.onClose))
            .subscribe(() => this.modalService.closeModal());
        return false;
    }

    public openModalViewSchool(): boolean {
        this.modalService.openModalWithComponent({
            type: ModalType.STANDARD,
            body: {
                title: this.currentSchool.name,
            },
            component: {
                componentRef: ViewSchoolComponent,
                parentIdRef: ModalParentIdEnum.BODY_CONTENT,
            },
        });
        return false;
    }

    public openModalSupportForm(): boolean {
        this.modalService.openModalWithComponent({
            type: ModalType.STANDARD,
            body: {
                title: 'NAV.HEADERS.MODAL.SUPPORT.TITLE',
            },
            component: {
                componentRef: SupportFormComponent,
                parentIdRef: ModalParentIdEnum.BODY_CONTENT,
                config: {
                    inputs: {
                        user: this.user,
                    },
                },
            },
        });
        return false;
    }

    private openModalBetaSupport(): void {
        this.modalService.openModalWithComponent({
            type: ModalType.STANDARD,
            body: {
                title: 'HOME.MODAL.SUPPORT.TITLE',
            },
            component: {
                componentRef: SupportBetaComponent,
                parentIdRef: ModalParentIdEnum.BODY_CONTENT,
            },
        });
    }

    private openModalPreventBack(): void {
        this.modalService.openModalWithComponent({
            type: ModalType.STANDARD,
            body: {
                title: 'SHARED.MODIFIED-DATA-TITLE',
                validateButtonFn: () => this.closePreventBack(true),
                validateButtonLabel: 'SHARED.YES',
                cancelButtonFn: () => this.closePreventBack(false),
                cancelButtonLabel: 'SHARED.NO',
            },
            component: {
                componentRef: PreventBackModalComponent,
                parentIdRef: ModalParentIdEnum.BODY_CONTENT,
                config: {
                    inputs: {
                        currentTarget: this.currentTarget,
                    },
                },
            },
        });
    }

    private selectUser(user: IUser): void {
        this.userToSelect = user;
        if (this.store.selectSnapshot(BackButtonState.getPreventGoBack)) {
            this.currentTarget = TargetChoicesEnum.IMPERSONATE;
            this.openModalPreventBack();
        } else {
            this.changeUser(this.userToSelect);
        }
    }
}
